import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getAccessLevelCapabilities } from '../config/posAccessLevels';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface POSProperty {
  id: string;
  property_name: string;
  property_code: string;
  hospitality_tier: string;
  contract_status: string;
  access_level?: string;
  group_id?: string;
  group_name?: string;
}

interface AccessLevelCapabilities {
  canManageManagers: boolean;
  canEditProperty: boolean;
  canEditRooms: boolean;
  canRequestService: boolean;
  canCancelService: boolean;
  canViewReports: boolean;
}

export interface POSContextValue {
  // Property selection
  selectedPropertyId: string;
  setSelectedPropertyId: (id: string) => void;
  clearProperty: () => void;
  selectedProperty: POSProperty | null;

  // User's accessible properties
  userProperties: POSProperty[];
  propertiesLoading: boolean;
  hasMultipleProperties: boolean;
  hasSingleProperty: boolean;
  hasNoProperties: boolean;

  // User mode flags
  isCMBStaff: boolean;
  isHotelManager: boolean;

  // Customer group access
  groups: { group_id: string; group_name: string }[];
  hasGroupAccess: boolean;

  // Permission helpers (based on access level)
  accessLevel: string | null;
  canEditProperty: boolean;
  canDeleteProperty: boolean;
  canManageStaff: boolean;
  canEditRooms: boolean;
  canRequestService: boolean;
  canCancelService: boolean;
  isViewer: boolean;

  // Refresh function (call after adding/removing properties)
  refreshProperties: () => Promise<void>;
}

const POSContext = createContext<POSContextValue | null>(null);

const STORAGE_KEY = 'pos_selected_property';

export function POSProvider({ children }: { children: ReactNode }) {
  const { user: effectiveUser, supabase } = useAuth();

  // Property selection with localStorage persistence
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || '';
  });

  // User's accessible properties (loaded based on mode)
  const [userProperties, setUserProperties] = useState<POSProperty[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedPropertyId) {
      localStorage.setItem(STORAGE_KEY, selectedPropertyId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedPropertyId]);

  // Determine user mode based on permissions
  const isCMBStaff = useMemo(() => {
    const perms = (effectiveUser?.permissions || {}) as Record<string, Record<string, unknown>>;
    // CMB staff: super admin OR global scope with any POS permission
    if (perms.admin?.super_admin === true) return true;
    const isGlobal = (perms as Record<string, unknown>)._scope === 'global' || perms.pos_services?.scope === 'all';
    const hasPOS = perms.pos_dashboard || perms.pos_properties || perms.pos_services || perms.pos_proposals;
    return isGlobal && !!hasPOS;
  }, [effectiveUser?.permissions]);

  const isHotelManager = useMemo(() => {
    const perms = (effectiveUser?.permissions || {}) as Record<string, Record<string, unknown>>;
    // Hotel manager: has pos_portal permission but is NOT CMB staff
    const posLevel = perms.pos_portal?.level ?? perms.pos_portal?.access; // support legacy { access: true }
    return (posLevel != null && posLevel !== 'off' && posLevel !== false) && !isCMBStaff;
  }, [effectiveUser?.permissions, isCMBStaff]);

  // Refresh trigger - increment to force reload
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load properties function (extracted so it can be called directly)
  const loadUserProperties = async () => {
    // Skip on Global portal - hosp_properties table doesn't exist there
    const countryCode = import.meta.env.VITE_COUNTRY_CODE;
    if (countryCode === 'GLOBAL') {
      setPropertiesLoading(false);
      setUserProperties([]);
      return;
    }

    if (!supabase || !effectiveUser?.id) {
      setPropertiesLoading(false);
      return;
    }

    setPropertiesLoading(true);

    try {

      if (isCMBStaff) {
        // CMB staff: load ALL properties (not just active - they need to manage pending too)
        const { data, error } = await (supabase as SupabaseClient)
          .from('hosp_properties')
          .select('id, property_name, property_code, hospitality_tier, contract_status')
          .order('property_name');


        if (error) {
          console.error('[POS] Failed to load properties:', error);
          setUserProperties([]);
        } else {
          setUserProperties((data as POSProperty[]) || []);
        }
      } else if (isHotelManager) {
        // Hotel manager: load from pos_user_properties view
        // This view includes both direct property access AND customer-group access
        const { data: viewData, error: viewError } = await (supabase as SupabaseClient)
          .from('pos_user_properties')
          .select('property_id, property_name, slug, address, access_level, group_name, group_id');

        if (viewError) {
          console.error('[POS] Failed to load user properties:', viewError);
          setUserProperties([]);
        } else {
          // Load full property details for each accessible property
          const propertyIds = (viewData || []).map((v: Record<string, unknown>) => v.property_id as string);
          if (propertyIds.length > 0) {
            const { data: propData } = await (supabase as SupabaseClient)
              .from('hosp_properties')
              .select('id, property_name, property_code, hospitality_tier, contract_status')
              .in('id', propertyIds);

            const accessMap = new Map<string, { access_level: string; group_id?: string; group_name?: string }>();
            for (const v of (viewData || []) as Array<Record<string, unknown>>) {
              accessMap.set(v.property_id as string, {
                access_level: v.access_level as string,
                group_id: v.group_id as string | undefined,
                group_name: v.group_name as string | undefined,
              });
            }

            const properties = (propData || [])
              .filter((p: Record<string, unknown>) => p.contract_status === 'active')
              .map((p: Record<string, unknown>) => {
                const meta = accessMap.get(p.id as string);
                return {
                  id: p.id as string,
                  property_name: p.property_name as string,
                  property_code: p.property_code as string,
                  hospitality_tier: p.hospitality_tier as string,
                  contract_status: p.contract_status as string,
                  access_level: meta?.access_level,
                  group_id: meta?.group_id,
                  group_name: meta?.group_name,
                } as POSProperty;
              });
            setUserProperties(properties);
          } else {
            setUserProperties([]);
          }
        }
      } else {
        // Territory-scoped CMB staff (Territory Manager / Ambassador with
        // pos_properties permission). They see the properties they are the
        // assigned_manager_id on. The field is enforced as required by the
        // pos-onboard edge function, so every property has an owner and
        // territory managers see exactly their patch.
        const perms = (effectiveUser?.permissions || {}) as Record<string, Record<string, unknown>>;
        const hasPOS = perms.pos_dashboard || perms.pos_properties || perms.pos_services || perms.pos_proposals;
        if (!hasPOS) {
          setUserProperties([]);
          return;
        }

        const { data, error } = await (supabase as SupabaseClient)
          .from('hosp_properties')
          .select('id, property_name, property_code, hospitality_tier, contract_status')
          .eq('assigned_manager_id', effectiveUser.id)
          .order('property_name');

        if (error) {
          console.error('[POS] Failed to load territory-scoped properties:', error);
          setUserProperties([]);
        } else {
          setUserProperties((data as POSProperty[]) || []);
        }
      }
    } catch (err: unknown) {
      console.error('[POS] Property load error:', err);
      setUserProperties([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Load properties based on user mode
  useEffect(() => {
    loadUserProperties();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [effectiveUser?.id, isCMBStaff, isHotelManager, supabase, refreshTrigger]);

  // Auto-select if user has only one property
  useEffect(() => {
    const singleProperty = userProperties[0];
    if (!propertiesLoading && userProperties.length === 1 && !selectedPropertyId && singleProperty) {
      setSelectedPropertyId(singleProperty.id);
    }
  }, [propertiesLoading, userProperties, selectedPropertyId]);

  // Clear selection if selected property is no longer in user's list
  useEffect(() => {
    if (!propertiesLoading && selectedPropertyId && userProperties.length > 0) {
      const stillValid = userProperties.some(p => p.id === selectedPropertyId);
      if (!stillValid) {
        setSelectedPropertyId('');
      }
    }
  }, [propertiesLoading, userProperties, selectedPropertyId]);

  // Find the currently selected property object
  const selectedProperty = useMemo(() => {
    return userProperties.find(p => p.id === selectedPropertyId) || null;
  }, [userProperties, selectedPropertyId]);

  // Get capabilities from the access level config
  const accessLevelCapabilities = useMemo<AccessLevelCapabilities>(() => {
    if (isCMBStaff) {
      // CMB staff has all capabilities
      return {
        canManageManagers: true,
        canEditProperty: true,
        canEditRooms: true,
        canRequestService: true,
        canCancelService: true,
        canViewReports: true
      };
    }
    return getAccessLevelCapabilities(selectedProperty?.access_level || 'viewer');
  }, [isCMBStaff, selectedProperty?.access_level]);

  // Permission checks using the config
  const canEditProperty = useMemo(() => {
    return accessLevelCapabilities.canEditProperty;
  }, [accessLevelCapabilities]);

  const canDeleteProperty = useMemo(() => {
    // Only CMB staff can delete properties (safety)
    return isCMBStaff;
  }, [isCMBStaff]);

  const canManageStaff = useMemo(() => {
    return accessLevelCapabilities.canManageManagers;
  }, [accessLevelCapabilities]);

  const canEditRooms = useMemo(() => {
    return accessLevelCapabilities.canEditRooms;
  }, [accessLevelCapabilities]);

  const canRequestService = useMemo(() => {
    return accessLevelCapabilities.canRequestService;
  }, [accessLevelCapabilities]);

  const canCancelService = useMemo(() => {
    return accessLevelCapabilities.canCancelService;
  }, [accessLevelCapabilities]);

  const isViewer = useMemo(() => {
    return !isCMBStaff && selectedProperty?.access_level === 'viewer';
  }, [isCMBStaff, selectedProperty?.access_level]);

  // Derive customer groups from properties
  const groups = useMemo(() => {
    const groupMap = new Map<string, string>();
    for (const p of userProperties) {
      if (p.group_id && p.group_name) {
        groupMap.set(p.group_id, p.group_name);
      }
    }
    return Array.from(groupMap.entries()).map(([group_id, group_name]) => ({
      group_id,
      group_name,
    }));
  }, [userProperties]);

  const hasGroupAccess = groups.length > 0 && !isCMBStaff;

  // Get the current access level label
  const accessLevel = useMemo(() => {
    if (isCMBStaff) return 'CMB Staff';
    return selectedProperty?.access_level || null;
  }, [isCMBStaff, selectedProperty?.access_level]);

  const value: POSContextValue = {
    // Property selection
    selectedPropertyId,
    setSelectedPropertyId,
    clearProperty: () => setSelectedPropertyId(''),
    selectedProperty,

    // User's accessible properties
    userProperties,
    propertiesLoading,
    hasMultipleProperties: userProperties.length > 1,
    hasSingleProperty: userProperties.length === 1,
    hasNoProperties: userProperties.length === 0,

    // User mode flags
    isCMBStaff,
    isHotelManager,

    // Customer group access
    groups,
    hasGroupAccess,

    // Permission helpers (based on access level)
    accessLevel,
    canEditProperty,
    canDeleteProperty,
    canManageStaff,
    canEditRooms,
    canRequestService,
    canCancelService,
    isViewer,

    // Refresh function (call after adding/removing properties)
    refreshProperties: async () => {
      setRefreshTrigger(prev => prev + 1);
    }
  };

  return (
    <POSContext.Provider value={value}>
      {children}
    </POSContext.Provider>
  );
}

export function usePOS(): POSContextValue {
  const context = useContext(POSContext);
  if (!context) {
    throw new Error('usePOS must be used within a POSProvider');
  }
  return context;
}

export default POSContext;
